import { Geometry, LineString, Point, Polygon} from 'ol/geom';
import { Vector as VectorLayer, VectorImage} from 'ol/layer';
import { Vector as VectorSource, Cluster } from 'ol/source';
import { Style, Fill, Stroke,Text, Icon, Circle as CircleStyle } from 'ol/style';
import { asArray as colorAsArray, asString as colorAsString } from 'ol/color';
import { Feature } from 'ol';
import { ICONS, OBJECT_TYPE, OBJECT_ID } from '../constants';
import { FeatureType, PathDirection } from '../enums';
import { COLORS } from '../constants';
import { transform } from 'ol/proj';
import { getDistance } from 'ol/sphere';

const DEFAULT_ICON_FONT = 'interRegular';
const DEFAULT_PATH_FONT = 'interRegular';
const DEFAULT_PATH_ICON_SIZE = 44;
const DEFAULT_POI_ICON_SIZE = 28;
const ICON_SELECTION_PADDING = 4;
const CLOSED_TEXT = 'Stängd';
// If your icon's "forward" direction in the PNG points somewhere other than
// the right-hand side, set this offset (radians). Example: if the icon faces
// up in the image, set ICON_ROTATION_OFFSET = -Math.PI/2 so the right side
// of the image will align with the path direction.
// The attached PNG has the bicycle pointing up, so rotate -90° to make the
// image's right side point along the path (to the right).
const ICON_ROTATION_OFFSET = -Math.PI / 2;
let renderScale = 0.5;

/**
 * Gets the render scale variable
 */
const getRenderScale = (): number => {
    return renderScale;
};

/**
 * Sets the render scale variable
 */
const setRenderScale = (scale: number) => {
    renderScale = scale;
};

// Cache for colored icon data URLs
const iconColorCache: {[key: string]: string} = {};

/**
 * Creates a colored version of a white PNG icon
 */
const getColoredIconDataUrl = (imageSrc: string, color: string): string => {
    const cacheKey = `${imageSrc}_${color}`;
    
    // Return cached version if available
    if (iconColorCache[cacheKey]) {
        return iconColorCache[cacheKey];
    }
    
    // If not in cache, return original (it will be cached asynchronously)
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) return;
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw the original image
        ctx.drawImage(img, 0, 0);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Parse the color
        const colorArray = colorAsArray(color);
        const r = colorArray[0];
        const g = colorArray[1];
        const b = colorArray[2];
        
        // Tint all non-transparent pixels with the specified color
        for (let i = 0; i < data.length; i += 4) {
            const alpha = data[i + 3];
            
            if (alpha > 0) {
                // Replace with the target color, maintaining the original alpha
                data[i] = r;
                data[i + 1] = g;
                data[i + 2] = b;
            }
        }
        
        // Put the modified image data back
        ctx.putImageData(imageData, 0, 0);
        
        // Cache the data URL
        iconColorCache[cacheKey] = canvas.toDataURL();
    };
    
    img.src = imageSrc;
    
    // Return original for first render
    return imageSrc;
};

/**
 * Creates a empty vector source
 */
const createEmptyVectorSource = (): VectorSource<Feature<Geometry>> => {
    return new VectorSource<Feature<Geometry>>({
        features: [],
    });
};

/**
 * Creates a empty vector layer for a source
 */
const createEmptyVectorLayer = (
    source: VectorSource<Feature<Geometry>>,
    styleFunction?: (feature: any, resolution: number) => any
): VectorImage => {
    return new VectorImage({
        source,
        ...(styleFunction && { style: styleFunction }),
    });
};

/**
 * Creates a cluster layer for the given vector source using the given icon path and color
 */
const createIconClusterLayer = (source: VectorSource<Feature<Geometry>>, path: string, color: string): VectorImage => {
    const cluster = new Cluster({
        distance: 40,
        source,
    });

    return new VectorImage({
        source: cluster,
        style: (feature) => {
            const size = feature.get('features').length;

            if (size === 1) {
                return feature.get('features')[0]['style_'];
            }

            return createBaseMapIconStyle(path, `(${size})`, color, 30, DEFAULT_ICON_FONT, 45, DEFAULT_PATH_ICON_SIZE);
        },
    });
};

/**
 * Creates a set of base styles for a icon
 */
const createBaseMapIconStyle = (
    iconPath: string,
    name: string | undefined,
    color: string,
    fontSize: number,
    fontFamily: string,
    textOffset: number,
    backplateSize: number,
    inverted: boolean = true,
    opacity: number = 1,
): Style[] => {
    return [
        // White circle backplate
        new Style({
            image: createCircleBackingPlateStyle(backplateSize, opacity),
        }),
        // Text label
        new Style({
            text: new Text({
                text: name,
                font: `${fontSize}px ${fontFamily}`,
                offsetY: textOffset * renderScale,
                scale: renderScale,
                stroke: new Stroke({
                    color: inverted ? '#000000' : '#FFFFFF',
                    width: 8,
                }),
                fill: new Fill({
                    color: inverted ? '#FFFFFF' : color,
                }),
            }),
        }),
        // Icon
        new Style({
            image: new Icon({
                src: iconPath,
                scale: renderScale,
                crossOrigin: 'anonymous',
                color,
                opacity,
            }),
        }),
    ];
};

/**
 * Creates a circle style, mainly used behind icons
 */
const createCircleBackingPlateStyle = (radius: number = 0, opacity: number = 1): CircleStyle => {
    return new CircleStyle({
        radius: radius * renderScale,
            fill: new Fill({
            color: colorWithAlpha('#FFFFFF', opacity),
        }),
    });
};

/**
 * Creates a new color using the given hex color string and a alpha value
 */
const colorWithAlpha = (color: string, alpha: number): string => {
    const [r, g, b] = Array.from(colorAsArray(color));
    return colorAsString([r, g, b, alpha]);
};

/**
 * Creates a base feature for a icon using the given style
 */
const createBaseMapIconFeature = (
    point: Point,
    style: Style | Style[],
    type?: string,
    id?: string,
): Feature<Geometry> => {
    const feature = new Feature<Geometry>({
        geometry: point,
    });
    // Store style as property instead of setting it directly
    // This allows layer-level style functions to control rendering based on zoom
    feature.set('__baseStyle', style);
    feature.set(OBJECT_TYPE, type);
    feature.set(OBJECT_ID, id);
    return feature;
};

/**
 * Creates a styled feature for a gps indicator icon
 */
const createGpsIndicatorFeature = (point: Point, heading?: number): Feature<Geometry> => {
    const feature = new Feature({
        geometry: point,
    });

    const style = new Style({
        image: new Icon({
            src: ICONS.GPS_POSITION,
            scale: renderScale,
            rotation: heading ?? 0,
        }),
    });
    feature.setStyle(style);
    return feature;
};

/**
 * Creates a styled feature for a snowmobile path icon
 */
const createSnowmobilingIconFeature = (
    point: Point,
    name: string | undefined,
    isBold: boolean = false,
): Feature<Geometry> => {
    return createBaseMapIconFeature(
        point,
        createBaseMapIconStyle(
            ICONS.PATHS.SNOWMOBILING,
            name,
            COLORS.PATHS.SNOWMOBILING,
            32,
            DEFAULT_ICON_FONT,
            64,
            isBold ? DEFAULT_PATH_ICON_SIZE + ICON_SELECTION_PADDING : DEFAULT_PATH_ICON_SIZE,
            false,
        ),
    );
};

/**
 * Creates a styled feature for a common path icon
 */
const createCommonPathIconFeature = (
    point: Point,
    id: string,
    name: string | undefined,
    color: string,
    iconPath: string,
    isBold: boolean = false
): Feature<Geometry> => {
    return createBaseMapIconFeature(
        point,
        createBaseMapIconStyle(
            iconPath,
            name,
            color,
            32,
            DEFAULT_ICON_FONT,
            64,
            isBold ? DEFAULT_PATH_ICON_SIZE + ICON_SELECTION_PADDING : DEFAULT_PATH_ICON_SIZE,
            false,
        ),
        FeatureType.Path,
        id,
    );
};

/**
 * Creates a styled feature for a common poi icon
 */
const createCommonPoiIconFeature = (
    point: Point,
    id: string,
    name: string | undefined,
    color: string,
    iconPath: string,
    inverted: boolean = false,
    isBold: boolean = false,
): Feature<Geometry> => {
    return createBaseMapIconFeature(
        point,
        createBaseMapIconStyle(
            iconPath,
            name,
            color,
            24,
            DEFAULT_ICON_FONT,
            42,
            isBold ? DEFAULT_POI_ICON_SIZE + ICON_SELECTION_PADDING : DEFAULT_POI_ICON_SIZE,
            inverted,
        ),
        FeatureType.Poi,
        id,
    );
};

/**
 * Creates a styled feature for a temperature poi icon with dynamic temperature text
 */
const createTemperaturePoiIconFeature = (
    point: Point,
    id: string,
    temperature: number,
    color: string,
    iconPath: string,
    inverted: boolean = false,
    isBold: boolean = false,
): Feature<Geometry> => {
    // Format temperature with one decimal place and add degree symbol
    const tempText = `${temperature.toFixed(1)}°`;
    
    // Calculate dimensions for the rounded rectangle
    const padding = 8 * renderScale;
    const fontSize = 32;
    const textWidth = tempText.length * 18 * renderScale; // Approximate width per character
    const boxWidth = textWidth + padding * 2;
    const boxHeight = 44 * renderScale;
    
    // Create canvas for rounded rectangle with text
    const canvas = document.createElement('canvas');
    canvas.width = boxWidth;
    canvas.height = boxHeight;
    const ctx = canvas.getContext('2d')!;
    
    // Draw rounded rectangle background
    const radius = 8 * renderScale;
    ctx.fillStyle = '#FFFFFF';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.15)';
    ctx.lineWidth = 1;
    
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(boxWidth - radius, 0);
    ctx.arcTo(boxWidth, 0, boxWidth, radius, radius);
    ctx.lineTo(boxWidth, boxHeight - radius);
    ctx.arcTo(boxWidth, boxHeight, boxWidth - radius, boxHeight, radius);
    ctx.lineTo(radius, boxHeight);
    ctx.arcTo(0, boxHeight, 0, boxHeight - radius, radius);
    ctx.lineTo(0, radius);
    ctx.arcTo(0, 0, radius, 0, radius);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Draw temperature text centered
    ctx.fillStyle = '#000000';
    ctx.font = `bold ${fontSize * renderScale}px ${DEFAULT_ICON_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(tempText, boxWidth / 2, boxHeight / 2);
    
    return createBaseMapIconFeature(
        point,
        [
            new Style({
                image: new Icon({
                    img: canvas,
                    scale: 1 / renderScale,
                    anchor: [0.5, 0.5],
                    opacity: 1,
                }),
            }),
        ],
        FeatureType.Poi,
        id,
    );
};

/**
 * Creates a set of base styles for a temperature icon with text overlay
 */
const createTemperatureIconStyle = (
    iconPath: string,
    temperatureText: string,
    color: string,
    fontSize: number,
    fontFamily: string,
    textOffset: number,
    backplateSize: number,
    inverted: boolean = true,
    opacity: number = 1,
): Style[] => {
    // Calculate dimensions for the rounded rectangle background
    const padding = 4 * renderScale; // Reduced padding for tighter layout
    const iconSize = 36 * renderScale;
    const textWidth = temperatureText.length * 14 * renderScale; // Approximate text width (adjusted for decimal)
    const boxWidth = iconSize + textWidth + padding * 2.5;
    const boxHeight = 44 * renderScale;
    
    // Create canvas for rounded rectangle with icon and text
    const canvas = document.createElement('canvas');
    canvas.width = boxWidth;
    canvas.height = boxHeight;
    const ctx = canvas.getContext('2d')!;
    
    // Draw rounded rectangle background
    const radius = 12 * renderScale;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;
    
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(boxWidth - radius, 0);
    ctx.arcTo(boxWidth, 0, boxWidth, radius, radius);
    ctx.lineTo(boxWidth, boxHeight - radius);
    ctx.arcTo(boxWidth, boxHeight, boxWidth - radius, boxHeight, radius);
    ctx.lineTo(radius, boxHeight);
    ctx.arcTo(0, boxHeight, 0, boxHeight - radius, radius);
    ctx.lineTo(0, radius);
    ctx.arcTo(0, 0, radius, 0, radius);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Draw temperature text (right side)
    ctx.fillStyle = '#2C3E50'; // Dark blue-gray color
    ctx.font = `bold ${fontSize * renderScale}px ${fontFamily}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(temperatureText, iconSize + padding * 2, boxHeight / 2);
    
    return [
        // Background with icon and text
        new Style({
            image: new Icon({
                img: canvas,
                scale: 1 / renderScale,
                anchor: [0.5, 0.5],
                opacity,
            }),
        }),
        // Thermometer icon (left side)
        new Style({
            image: new Icon({
                src: iconPath,
                scale: 1.0 * renderScale,
                crossOrigin: 'anonymous',
                color: '#2C3E50',
                anchor: [1.8, 0.5], // Position icon on left side of box
                opacity,
            }),
        }),
    ];
};

/**
 * Creates a styled feature for a common aoi icon
 */
const createCommonAoiIconFeature = (
    point: Point,
    id: string,
    name: string | undefined,
    color: string,
    iconPath: string,
    inverted: boolean = false,
    isBold: boolean = false,
): Feature<Geometry> => {
    return createBaseMapIconFeature(
        point,
        createBaseMapIconStyle(
            iconPath,
            name,
            color,
            24,
            DEFAULT_ICON_FONT,
            42,
            isBold ? DEFAULT_POI_ICON_SIZE + ICON_SELECTION_PADDING : DEFAULT_POI_ICON_SIZE,
            inverted,
        ),
        FeatureType.Aoi,
        id,
    );
};

/**
 * Creates a styled feature for a path endcap icon
 */
const createEndcapIconFeature = (
    point: Point, 
    name: string | undefined, 
    inverted: boolean, 
    color: string = COLORS.PATHS.ENDCAP,
    opacity: number = 1
): Feature<Geometry> => {
    return createBaseMapIconFeature(
        point,
        createBaseMapIconStyle(
            ICONS.PATHS.ENDCAP,
            name,
            color,
            20,
            DEFAULT_ICON_FONT,
            30,
            12,
            inverted,
            opacity,
        ),
    );
};

/**
 * Creates a set of base styles for paths
 */
const createBasePathStyles = (
    color: string,
    opacity: number,
    width: number = 6,
    outline: number = 8,
): Style[] => {
    return [
        new Style({
            stroke: new Stroke({
                color: colorWithAlpha('#FFFFFF', opacity), // Apply opacity to white outline too
                width: (width + outline) * renderScale,
            }),
        }),
        new Style({
            stroke: new Stroke({
                color: colorWithAlpha(color, opacity),
                width: width * renderScale,
            }),
        }),
    ];
};

/**
 * Creates a set of base styles for aois
 */
const createBaseAoisStyles = (
    color: string,
    opacity: number = 0.8,
    width: number = 6,
    outline: number = 8,
): Style[] => {
    return [
        new Style({
            stroke: new Stroke({
              color: color,
              width: (width + outline) * renderScale,
            }),
          }),
          new Style({
            fill: new Fill({color: colorWithAlpha(color, opacity)}),
          }),
    ];
};

/**
 * Creates a base text style for paths
 */
const createBasePathTextStyle = (color: string, name?: string): Style => {
    return new Style({
        text: new Text({
            text: name,
            font: `20px ${DEFAULT_PATH_FONT}`,
            scale: renderScale,
            stroke: new Stroke({
                color: '#FFFFFF',
                width: 8,
            }),
            fill: new Fill({
                color,
            }),
        }),
    });
};

/**
 * Creates a set of styled base featureas for a path using the given style
 */
const createBasePathFeatures = (
    coordinates: number[][],
    type: string,
    id: string,
    texts: {color: string, name: string, position: number}[],
    pathStyle: Style | Style[],
    pathDirection?: PathDirection,
): Feature<Geometry>[] => {
    const features = [];
    const pathFeature = new Feature({
        geometry: new LineString(coordinates, 'XYM'),
    });
    pathFeature.set(OBJECT_TYPE, type);
    pathFeature.set(OBJECT_ID, id);
    pathFeature.setStyle(pathStyle);
    features.push(pathFeature);

    // Place arrows along path
    if (pathDirection !== undefined) {
        const absArrowSpacing = 150;
        let traversedSpace = 0;
        coordinates.forEach((current, index) => {
            if (index > 0) {
                const previous = coordinates[index - 1];
        
                const projection = 'EPSG:4326';
                const sourceProj = 'EPSG:3857';
                const c1 = transform(previous, sourceProj, projection);
                const c2 = transform(current, sourceProj, projection);
                const dist = getDistance(c1, c2);

                // Check if we've gone long enough along path without an arrow
                if (Math.ceil(traversedSpace / absArrowSpacing) < Math.ceil((traversedSpace + dist) / absArrowSpacing)) {
                    const angOfs = pathDirection === PathDirection.Reverse ? Math.PI : 0;
                    // Use dy,dx ordering so rotation is the angle of the path vector
                    // (OpenLayers expects rotation in radians). Add ICON_ROTATION_OFFSET
                    // to compensate if the source image's forward direction is not to the
                    // right-hand side of the image.
                    const rotation = Math.atan2(current[1] - previous[1], current[0] - previous[0]) + angOfs + ICON_ROTATION_OFFSET;
                    const arrowFeature = new Feature({
                        geometry: new Point(current),
                    });

                    const arrowStyle = new Style({
                        image: new Icon({
                            src: ICONS.DIRECTION_ARROW,
                            scale: renderScale,
                            rotation,
                            // rotate around image center
                            anchor: [0.5, 0.5],
                            anchorXUnits: 'fraction',
                            anchorYUnits: 'fraction',
                            rotateWithView: false,
                        }),
                    });
                    arrowFeature.setStyle(arrowStyle);
    
                    features.push(arrowFeature);
                }
                traversedSpace += dist;
            }
        });
    }
    
    texts.forEach(text => {
        const index = Math.floor(coordinates.length * text.position);
        const textFeature = new Feature({
            geometry: new Point(coordinates[index]),
        });
        textFeature.setStyle(createBasePathTextStyle(text.color, text.name));
        features.push(textFeature);
    });
    
    return features;
};

/**
 * Creates icon features along a path at regular intervals
 */
const createPathIconFeatures = (
    coordinates: number[][],
    iconPath: string,
    color: string,
    iconSpacing: number = 250, // meters between icons
    opacity: number = 1,
): Feature<Geometry>[] => {
    const features: Feature<Geometry>[] = [];
    let traversedSpace = 0;
    
    coordinates.forEach((current, index) => {
        if (index > 0) {
            const previous = coordinates[index - 1];
    
            const projection = 'EPSG:4326';
            const sourceProj = 'EPSG:3857';
            const c1 = transform(previous, sourceProj, projection);
            const c2 = transform(current, sourceProj, projection);
            const dist = getDistance(c1, c2);

            // Check if we've gone long enough along path without an icon
            if (Math.ceil(traversedSpace / iconSpacing) < Math.ceil((traversedSpace + dist) / iconSpacing)) {
                // Keep path icons horizontal — do not rotate per-segment so the
                // PNG stays upright regardless of path direction.
                const iconFeature = new Feature({
                    geometry: new Point(current),
                });

                // Get colored version of the icon
                const coloredIconSrc = getColoredIconDataUrl(iconPath, color);

                // Instead of setting a per-feature style (which would override
                // layer style functions), store the icon properties on the
                // feature. The layer style function will construct the
                // ol/style/Style when the feature should be rendered.
                iconFeature.set('iconSrc', coloredIconSrc);
                iconFeature.set('iconScale', renderScale * 0.7);
                // Anchor so the 'wheel' or lower part sits on the path
                iconFeature.set('iconAnchor', [0.5, 0.85]);
                iconFeature.set('iconAnchorUnits', 'fraction');
                iconFeature.set('iconOpacity', opacity);

                features.push(iconFeature);
            }
            traversedSpace += dist;
        }
    });
    
    return features;
};

/**
 * Creates a set of styled features for a snowmobile path
 */
const createSnowmobilePathFeatures = (
    coordinates: number[][],
    id: string,
    isClosed: boolean,
    isBold: boolean,
): Feature<Geometry>[] => {
    return createBasePathFeatures(
        coordinates,
        FeatureType.SnowmobileSubPath,
        id,
        isClosed
            ? [{name: CLOSED_TEXT, position: 0.5, color: COLORS.PATHS.SNOWMOBILING}]
            : [],
        createBasePathStyles(
            COLORS.PATHS.SNOWMOBILING,
            isClosed ? 0.3 : 1,
            6,
            isBold ? 16 : 8,
        ),
    );
};

/**
 * Creates a set of styled features for a common path
 */
const createCommonPathFeatures = (
    coordinates: number[][],
    id: string,
    color: string,
    isClosed: boolean,
    isBold: boolean,
    pathDirection?: PathDirection,
): Feature<Geometry>[] => {
    // Keep original color but reduce opacity to 30% for closed paths
    return createBasePathFeatures(
        coordinates,
        FeatureType.Path,
        id,
        [], // No text labels for closed paths
        createBasePathStyles(color, isClosed ? 0.3 : 1, 6, isBold ? 16 : 8),
        pathDirection,
    );
};

/**
 * Creates a set of styled base featureas for an aoi using the given style
 */
const createBaseAoiFeatures = (
    coordinates: number[][],
    type: string,
    id: string,
    aoiStyle: Style | Style[],
): Feature<Geometry>[] => {
    const features = [];
    const aoiFeature = new Feature({
        geometry: new Polygon([coordinates]),
    });
    aoiFeature.set(OBJECT_TYPE, type);
    aoiFeature.set(OBJECT_ID, id);
    aoiFeature.setStyle(aoiStyle);
    features.push(aoiFeature);
   
    return features;
};

/**
 * Creates a set of styled features for a common aoi
 */
const createCommonAoiFeatures = (
    coordinates: number[][],
    id: string,
    color: string,
    
): Feature<Geometry>[] => {
    return createBaseAoiFeatures(
        coordinates,
        FeatureType.Aoi,
        id,
        createBaseAoisStyles(color),
    );
};

/**
 * Post a message to the host app
 */
const postMessage = (msg: string) => {
    if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(msg);
    }
};

export {
    getRenderScale,
    setRenderScale,
    createEmptyVectorSource,
    createEmptyVectorLayer,
    createIconClusterLayer,
    createGpsIndicatorFeature,
    createSnowmobilingIconFeature,
    createCommonPathIconFeature,
    createPathIconFeatures,
    createCommonPoiIconFeature,
    createTemperaturePoiIconFeature,
    createCommonAoiIconFeature,
    createEndcapIconFeature,
    createSnowmobilePathFeatures,
    createCommonPathFeatures,
    postMessage,
    createCommonAoiFeatures
};